import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LogOut,
  Mail,
  Phone,
  Building2,
  Globe,
  MapPin,
  Calendar,
  Eye,
  Trash2,
  CheckCircle,
  Clock,
  Filter,
  RefreshCcw,
  Plus,
  Edit,
  X,
  Image,
  Search,
  Copy,
  Lock,
  Unlock,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import logo from "../assets/logo.svg";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [activeTab, setActiveTab] = useState("enquiries");

  // Enquiries state
  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, uk: 0, international: 0, korea: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);

  // Projects state
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectSearch, setProjectSearch] = useState("");
  const [projectForm, setProjectForm] = useState({
    title: "",
    title_tr: "",
    date: new Date().toISOString().split("T")[0],
    description: "",
    description_tr: "",
    images: [""],
  });

  // Lots state (Trade Portal)
  const [lots, setLots] = useState([]);
  const [lotsLoading, setLotsLoading] = useState(false);
  const [showLotModal, setShowLotModal] = useState(false);
  const [editingLot, setEditingLot] = useState(null);
  const [lotSearch, setLotSearch] = useState("");
  const [lotForm, setLotForm] = useState({
    title: "",
    brand: "",
    wattage: "",
    quantity: "",
    condition: "",
    location: "",
    description: "",
    photos: [""],
  });
  const [newLotCode, setNewLotCode] = useState(null);
  const [showOffersModal, setShowOffersModal] = useState(false);
  const [offersLot, setOffersLot] = useState(null);
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/login`, { password });
      setToken(res.data.token);
      setIsLoggedIn(true);
      localStorage.setItem("admin_token", res.data.token);
      toast.success("Logged in");
    } catch {
      toast.error("Invalid password");
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setToken("");
    localStorage.removeItem("admin_token");
  };

  // Enquiries functions
  const fetchEnquiries = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [enquiriesRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/enquiries${filter !== "all" ? `?enquiry_type=${filter}` : ""}`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/enquiries/stats`, { headers }),
      ]);
      setEnquiries(enquiriesRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error("Failed to fetch enquiries");
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  const updateEnquiryStatus = async (id, status) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/enquiries/${id}?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Status updated");
      fetchEnquiries();
    } catch {
      toast.error("Failed to update");
    }
  };

  const deleteEnquiry = async (id) => {
    if (!window.confirm("Are you sure you want to delete this enquiry?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/enquiries/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Enquiry deleted");
      setSelectedEnquiry(null);
      fetchEnquiries();
    } catch {
      toast.error("Failed to delete");
    }
  };

  // Projects functions
  const fetchProjects = useCallback(async () => {
    if (!token) return;
    setProjectsLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/projects`);
      setProjects(res.data);
    } catch {
      toast.error("Failed to fetch projects");
    } finally {
      setProjectsLoading(false);
    }
  }, [token]);

  const resetProjectForm = () => {
    setProjectForm({
      title: "",
      title_tr: "",
      date: new Date().toISOString().split("T")[0],
      description: "",
      description_tr: "",
      images: [""],
    });
    setEditingProject(null);
  };

  const openProjectModal = (project = null) => {
    if (project) {
      setEditingProject(project);
      setProjectForm({
        title: project.title,
        title_tr: project.title_translations?.tr || "",
        date: project.date,
        description: project.description,
        description_tr: project.description_translations?.tr || "",
        images: project.images && project.images.length > 0 ? project.images : [""],
      });
    } else {
      resetProjectForm();
    }
    setShowProjectModal(true);
  };

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Filter out empty image URLs
      const cleanedImages = projectForm.images.filter(url => url.trim() !== "");
      // Merge into the existing translations map (if editing) rather than
      // replacing it outright — this form only exposes a Turkish field, and
      // a post can carry other languages' translations written elsewhere
      // (e.g. via a script) that this submit must not wipe out.
      const { title_tr, description_tr, ...rest } = projectForm;
      const submitData = {
        ...rest,
        images: cleanedImages,
        title_translations: { ...(editingProject?.title_translations || {}), tr: title_tr },
        description_translations: { ...(editingProject?.description_translations || {}), tr: description_tr },
      };

      if (editingProject) {
        await axios.patch(`${BACKEND_URL}/api/admin/projects/${editingProject.id}`, submitData, { headers });
        toast.success("Post updated");
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/projects`, submitData, { headers });
        toast.success("Post created");
      }
      setShowProjectModal(false);
      resetProjectForm();
      fetchProjects();
    } catch {
      toast.error("Failed to save post");
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/projects/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Post deleted");
      fetchProjects();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const addImageField = () => {
    setProjectForm(prev => ({ ...prev, images: [...prev.images, ""] }));
  };

  const removeImageField = (index) => {
    setProjectForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const updateImageField = (index, value) => {
    setProjectForm(prev => ({
      ...prev,
      images: prev.images.map((img, i) => i === index ? value : img)
    }));
  };

  const isProjectFormValid = () => {
    return projectForm.title && projectForm.date && projectForm.description;
  };

  // Filter projects by search
  const filteredProjects = projects.filter(p =>
    p.title.toLowerCase().includes(projectSearch.toLowerCase()) ||
    p.date.includes(projectSearch)
  );

  // Lots functions (Trade Portal)
  const fetchLots = useCallback(async () => {
    if (!token) return;
    setLotsLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/lots`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLots(res.data);
    } catch {
      toast.error("Failed to fetch lots");
    } finally {
      setLotsLoading(false);
    }
  }, [token]);

  const resetLotForm = () => {
    setLotForm({
      title: "",
      brand: "",
      wattage: "",
      quantity: "",
      condition: "",
      location: "",
      description: "",
      photos: [""],
    });
    setEditingLot(null);
  };

  const openLotModal = (lot = null) => {
    if (lot) {
      setEditingLot(lot);
      setLotForm({
        title: lot.title,
        brand: lot.brand,
        wattage: lot.wattage,
        quantity: lot.quantity,
        condition: lot.condition,
        location: lot.location,
        description: lot.description,
        photos: lot.photos && lot.photos.length > 0 ? lot.photos : [""],
      });
    } else {
      resetLotForm();
    }
    setShowLotModal(true);
  };

  const handleLotSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Filter out empty photo URLs
      const cleanedPhotos = lotForm.photos.filter(url => url.trim() !== "");
      const submitData = { ...lotForm, photos: cleanedPhotos };

      if (editingLot) {
        await axios.patch(`${BACKEND_URL}/api/admin/lots/${editingLot.id}`, submitData, { headers });
        toast.success("Lot updated");
        setShowLotModal(false);
        resetLotForm();
        fetchLots();
      } else {
        const res = await axios.post(`${BACKEND_URL}/api/admin/lots`, submitData, { headers });
        toast.success("Lot created");
        setShowLotModal(false);
        resetLotForm();
        fetchLots();
        setNewLotCode(res.data.access_code);
      }
    } catch {
      toast.error("Failed to save lot");
    }
  };

  const closeLot = async (id) => {
    if (!window.confirm("Mark this lot as closed? Buyers will no longer be able to view it or submit offers.")) return;
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/lots/${id}`, { status: "closed" }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Lot closed");
      fetchLots();
    } catch {
      toast.error("Failed to update lot");
    }
  };

  const reopenLot = async (id) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/lots/${id}`, { status: "active" }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Lot reopened");
      fetchLots();
    } catch {
      toast.error("Failed to update lot");
    }
  };

  const deleteLot = async (id) => {
    if (!window.confirm("Are you sure you want to delete this lot? This cannot be undone.")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/lots/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Lot deleted");
      fetchLots();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const addPhotoField = () => {
    setLotForm(prev => ({ ...prev, photos: [...prev.photos, ""] }));
  };

  const removePhotoField = (index) => {
    setLotForm(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const updatePhotoField = (index, value) => {
    setLotForm(prev => ({
      ...prev,
      photos: prev.photos.map((img, i) => i === index ? value : img)
    }));
  };

  const isLotFormValid = () => {
    return (
      lotForm.title && lotForm.brand && lotForm.wattage && lotForm.quantity &&
      lotForm.condition && lotForm.location && lotForm.description
    );
  };

  // Filter lots by search
  const filteredLots = lots.filter(l =>
    l.title.toLowerCase().includes(lotSearch.toLowerCase()) ||
    l.access_code.toLowerCase().includes(lotSearch.toLowerCase())
  );

  const openOffersModal = async (lot) => {
    setOffersLot(lot);
    setShowOffersModal(true);
    setOffersLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/lots/${lot.id}/offers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const sorted = [...res.data].sort((a, b) => b.offer_amount - a.offer_amount);
      setOffers(sorted);
    } catch {
      toast.error("Failed to fetch offers");
    } finally {
      setOffersLoading(false);
    }
  };

  const copyAccessCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Access code copied");
  };

  useEffect(() => {
    const savedToken = localStorage.getItem("admin_token");
    if (savedToken) {
      setToken(savedToken);
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && token) {
      if (activeTab === "enquiries") {
        fetchEnquiries();
      } else if (activeTab === "projects") {
        fetchProjects();
      } else if (activeTab === "lots") {
        fetchLots();
      }
    }
  }, [isLoggedIn, token, filter, activeTab, fetchEnquiries, fetchProjects, fetchLots]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  const formatPostDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric"
    });
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "uk": return "bg-blue-100 text-blue-700";
      case "international": return "bg-green-100 text-green-700";
      case "korea": return "bg-purple-100 text-purple-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "new": return "bg-gold/20 text-gold";
      case "contacted": return "bg-blue-100 text-blue-700";
      case "closed": return "bg-green-100 text-green-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md"
        >
          <div className="text-center mb-8">
            <img src={logo} alt="MA Galaxy" className="h-16 mx-auto mb-4" loading="eager" />
            <h1 className="text-2xl font-bold text-navy">Admin Dashboard</h1>
            <p className="text-slate-500 mt-2">Enter your password to continue</p>
          </div>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin Password"
              className="w-full px-4 py-3 border border-slate-200 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-gold"
              data-testid="admin-password-input"
            />
            <button
              type="submit"
              className="w-full bg-gold text-navy font-bold py-3 rounded-lg hover:bg-gold-light transition-colors"
              data-testid="admin-login-button"
            >
              Login
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={logo} alt="MA Galaxy" className="h-10" loading="eager" />
            <h1 className="text-xl font-bold text-navy">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-green-600 flex items-center gap-1">
              <CheckCircle size={16} /> Logged in
            </span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-red-600 transition-colors"
              data-testid="admin-logout-button"
            >
              <LogOut size={18} /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab("enquiries")}
              className={`py-4 border-b-2 font-medium transition-colors ${
                activeTab === "enquiries"
                  ? "border-gold text-navy"
                  : "border-transparent text-slate-500 hover:text-navy"
              }`}
              data-testid="enquiries-tab"
            >
              Enquiries
            </button>
            <button
              onClick={() => setActiveTab("projects")}
              className={`py-4 border-b-2 font-medium transition-colors ${
                activeTab === "projects"
                  ? "border-gold text-navy"
                  : "border-transparent text-slate-500 hover:text-navy"
              }`}
              data-testid="projects-tab"
            >
              Projects
            </button>
            <button
              onClick={() => setActiveTab("lots")}
              className={`py-4 border-b-2 font-medium transition-colors ${
                activeTab === "lots"
                  ? "border-gold text-navy"
                  : "border-transparent text-slate-500 hover:text-navy"
              }`}
              data-testid="lots-tab"
            >
              Trade Portal
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === "enquiries" ? (
          // Enquiries Tab
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <p className="text-slate-500 text-sm">Total</p>
                <p className="text-3xl font-bold text-navy">{stats.total}</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <p className="text-slate-500 text-sm">New</p>
                <p className="text-3xl font-bold text-gold">{stats.new}</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <p className="text-slate-500 text-sm">UK</p>
                <p className="text-3xl font-bold text-blue-600">{stats.uk}</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <p className="text-slate-500 text-sm">International</p>
                <p className="text-3xl font-bold text-green-600">{stats.international}</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <p className="text-slate-500 text-sm">Korea</p>
                <p className="text-3xl font-bold text-purple-600">{stats.korea}</p>
              </div>
            </div>

            {/* Filter & Refresh */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Filter size={18} className="text-slate-400" />
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                  data-testid="enquiry-filter"
                >
                  <option value="all">All Enquiries</option>
                  <option value="uk">UK Only</option>
                  <option value="international">International Only</option>
                  <option value="korea">Korea Only</option>
                </select>
              </div>
              <button
                onClick={fetchEnquiries}
                className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-navy transition-colors"
                data-testid="refresh-enquiries"
              >
                <RefreshCcw size={18} /> Refresh
              </button>
            </div>

            {/* Enquiries Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">TYPE</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">NAME</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">COMPANY</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">EMAIL</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">DATE</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">STATUS</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-slate-400">Loading...</td>
                    </tr>
                  ) : enquiries.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-slate-400">No enquiries found</td>
                    </tr>
                  ) : (
                    enquiries.map((e) => (
                      <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium uppercase ${getTypeColor(e.enquiry_type)}`}>
                            {e.enquiry_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-medium text-navy">{e.name}</td>
                        <td className="px-6 py-4 text-slate-600">{e.company}</td>
                        <td className="px-6 py-4 text-slate-600">{e.email}</td>
                        <td className="px-6 py-4 text-slate-500 text-sm">{formatDate(e.created_at)}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium uppercase ${getStatusColor(e.status)}`}>
                            {e.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setSelectedEnquiry(e)}
                              className="p-2 text-slate-400 hover:text-navy transition-colors"
                              title="View"
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              onClick={() => deleteEnquiry(e.id)}
                              className="p-2 text-slate-400 hover:text-red-600 transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : activeTab === "projects" ? (
          // Projects Tab
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    placeholder="Search by title or date..."
                    className="pl-10 pr-4 py-2 border border-slate-200 rounded-lg w-64 focus:outline-none focus:ring-2 focus:ring-gold"
                    data-testid="project-search"
                  />
                </div>
                <span className="text-slate-500 text-sm">{filteredProjects.length} posts</span>
              </div>
              <button
                onClick={() => openProjectModal()}
                className="flex items-center gap-2 px-4 py-2 bg-gold text-navy font-medium rounded-lg hover:bg-gold-light transition-colors"
                data-testid="add-project-button"
              >
                <Plus size={18} /> Add Post
              </button>
            </div>

            {/* Projects Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">IMAGE</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">TITLE</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">DATE</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">IMAGES</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {projectsLoading ? (
                    <tr>
                      <td colSpan={5} className="text-center py-12 text-slate-400">Loading...</td>
                    </tr>
                  ) : filteredProjects.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-12 text-slate-400">
                        {projectSearch ? "No matching posts found" : "No posts yet. Click 'Add Post' to create one."}
                      </td>
                    </tr>
                  ) : (
                    filteredProjects.map((p) => (
                      <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-6 py-4">
                          {p.images && p.images.length > 0 ? (
                            <img
                              src={p.images[0]}
                              alt=""
                              className="w-12 h-12 object-cover rounded-lg"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                              <Image size={20} className="text-slate-400" />
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-medium text-navy line-clamp-1">{p.title}</p>
                          <p className="text-sm text-slate-400 line-clamp-1">{p.description.substring(0, 60)}...</p>
                        </td>
                        <td className="px-6 py-4 text-slate-600">{formatPostDate(p.date)}</td>
                        <td className="px-6 py-4 text-slate-600">{p.images?.length || 0}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => openProjectModal(p)}
                              className="p-2 text-slate-400 hover:text-navy transition-colors"
                              title="Edit"
                            >
                              <Edit size={18} />
                            </button>
                            <button
                              onClick={() => deleteProject(p.id)}
                              className="p-2 text-slate-400 hover:text-red-600 transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          // Lots Tab (Trade Portal)
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={lotSearch}
                    onChange={(e) => setLotSearch(e.target.value)}
                    placeholder="Search by title or access code..."
                    className="pl-10 pr-4 py-2 border border-slate-200 rounded-lg w-64 focus:outline-none focus:ring-2 focus:ring-gold"
                    data-testid="lot-search"
                  />
                </div>
                <span className="text-slate-500 text-sm">{filteredLots.length} lots</span>
              </div>
              <button
                onClick={() => openLotModal()}
                className="flex items-center gap-2 px-4 py-2 bg-gold text-navy font-medium rounded-lg hover:bg-gold-light transition-colors"
                data-testid="add-lot-button"
              >
                <Plus size={18} /> Add Lot
              </button>
            </div>

            {/* Lots Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">PHOTO</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">TITLE</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">ACCESS CODE</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">STATUS</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">CREATED</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {lotsLoading ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-slate-400">Loading...</td>
                    </tr>
                  ) : filteredLots.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-slate-400">
                        {lotSearch ? "No matching lots found" : "No lots yet. Click 'Add Lot' to create one."}
                      </td>
                    </tr>
                  ) : (
                    filteredLots.map((l) => (
                      <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-6 py-4">
                          {l.photos && l.photos.length > 0 ? (
                            <img
                              src={l.photos[0]}
                              alt=""
                              className="w-12 h-12 object-cover rounded-lg"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                              <Image size={20} className="text-slate-400" />
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-medium text-navy line-clamp-1">{l.title}</p>
                          <p className="text-sm text-slate-400 line-clamp-1">{l.brand} · {l.wattage} · Qty {l.quantity}</p>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => copyAccessCode(l.access_code)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-lg font-mono text-sm text-navy hover:bg-slate-200 transition-colors"
                            title="Copy access code"
                          >
                            {l.access_code} <Copy size={14} className="text-slate-400" />
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium uppercase ${
                            l.status === "active" ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600"
                          }`}>
                            {l.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-slate-500 text-sm">{formatDate(l.created_at)}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => openOffersModal(l)}
                              className="p-2 text-slate-400 hover:text-navy transition-colors"
                              title="View Offers"
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              onClick={() => openLotModal(l)}
                              className="p-2 text-slate-400 hover:text-navy transition-colors"
                              title="Edit"
                            >
                              <Edit size={18} />
                            </button>
                            {l.status === "active" ? (
                              <button
                                onClick={() => closeLot(l.id)}
                                className="p-2 text-slate-400 hover:text-amber-600 transition-colors"
                                title="Mark Closed"
                              >
                                <Lock size={18} />
                              </button>
                            ) : (
                              <button
                                onClick={() => reopenLot(l.id)}
                                className="p-2 text-slate-400 hover:text-green-600 transition-colors"
                                title="Reopen"
                              >
                                <Unlock size={18} />
                              </button>
                            )}
                            <button
                              onClick={() => deleteLot(l.id)}
                              className="p-2 text-slate-400 hover:text-red-600 transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Enquiry Detail Modal */}
      <AnimatePresence>
        {selectedEnquiry && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setSelectedEnquiry(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-navy">Enquiry Details</h2>
                <button onClick={() => setSelectedEnquiry(null)} className="text-slate-400 hover:text-slate-600">
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium uppercase ${getTypeColor(selectedEnquiry.enquiry_type)}`}>
                    {selectedEnquiry.enquiry_type}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium uppercase ${getStatusColor(selectedEnquiry.status)}`}>
                    {selectedEnquiry.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Building2 size={16} className="text-slate-400" />
                    <span className="font-medium">{selectedEnquiry.name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Building2 size={16} className="text-slate-400" />
                    <span>{selectedEnquiry.company}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Mail size={16} className="text-slate-400" />
                    <a href={`mailto:${selectedEnquiry.email}`} className="text-gold hover:underline">{selectedEnquiry.email}</a>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Phone size={16} className="text-slate-400" />
                    <a href={`tel:${selectedEnquiry.phone}`} className="text-gold hover:underline">{selectedEnquiry.phone}</a>
                  </div>
                  {selectedEnquiry.country && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Globe size={16} className="text-slate-400" />
                      <span>{selectedEnquiry.country}</span>
                    </div>
                  )}
                  {selectedEnquiry.location && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <MapPin size={16} className="text-slate-400" />
                      <span>{selectedEnquiry.location}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar size={16} className="text-slate-400" />
                    <span>{formatDate(selectedEnquiry.created_at)}</span>
                  </div>
                </div>

                {selectedEnquiry.panel_count && (
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-sm text-slate-500">Panels: {selectedEnquiry.panel_count}</p>
                    {selectedEnquiry.panel_brand && <p className="text-sm text-slate-500">Brand: {selectedEnquiry.panel_brand}</p>}
                  </div>
                )}

                {selectedEnquiry.volume_required && (
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-sm text-slate-500">Volume Required: {selectedEnquiry.volume_required}</p>
                  </div>
                )}

                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-slate-500 mb-2">Message:</p>
                  <p className="text-slate-700 whitespace-pre-wrap">{selectedEnquiry.message}</p>
                </div>

                <div className="flex gap-2 pt-4">
                  <button
                    onClick={() => updateEnquiryStatus(selectedEnquiry.id, "new")}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${selectedEnquiry.status === "new" ? "bg-gold text-navy" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                  >
                    New
                  </button>
                  <button
                    onClick={() => updateEnquiryStatus(selectedEnquiry.id, "contacted")}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${selectedEnquiry.status === "contacted" ? "bg-blue-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                  >
                    Contacted
                  </button>
                  <button
                    onClick={() => updateEnquiryStatus(selectedEnquiry.id, "closed")}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${selectedEnquiry.status === "closed" ? "bg-green-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                  >
                    Closed
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Project Modal */}
      <AnimatePresence>
        {showProjectModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowProjectModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-navy">
                  {editingProject ? "Edit Post" : "Add New Post"}
                </h2>
                <button onClick={() => setShowProjectModal(false)} className="text-slate-400 hover:text-slate-600">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleProjectSubmit} className="space-y-6">
                {/* Title */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Title (English) *</label>
                    <input
                      type="text"
                      value={projectForm.title}
                      onChange={(e) => setProjectForm({ ...projectForm, title: e.target.value })}
                      placeholder="e.g. Hanwha Q Cells Shipment to Dubai"
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Title (Turkish)</label>
                    <input
                      type="text"
                      value={projectForm.title_tr}
                      onChange={(e) => setProjectForm({ ...projectForm, title_tr: e.target.value })}
                      placeholder="Optional — falls back to English if left blank"
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold"
                    />
                  </div>
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Date *</label>
                  <input
                    type="date"
                    value={projectForm.date}
                    onChange={(e) => setProjectForm({ ...projectForm, date: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold"
                    required
                  />
                </div>

                {/* Description */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Description / Story (English) *</label>
                    <textarea
                      value={projectForm.description}
                      onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                      placeholder="Write the full story of this shipment or project. Include details about the panels, the destination, the logistics, what made it interesting or challenging. You can write as much as you want."
                      rows={8}
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold resize-y"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Description / Story (Turkish)</label>
                    <textarea
                      value={projectForm.description_tr}
                      onChange={(e) => setProjectForm({ ...projectForm, description_tr: e.target.value })}
                      placeholder="Optional — falls back to English if left blank"
                      rows={8}
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold resize-y"
                    />
                  </div>
                </div>

                {/* Images */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Images</label>
                  <div className="space-y-3">
                    {projectForm.images.map((img, index) => (
                      <div key={index} className="flex gap-2">
                        <div className="flex-1">
                          <input
                            type="url"
                            value={img}
                            onChange={(e) => updateImageField(index, e.target.value)}
                            placeholder="Paste image URL here"
                            className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold"
                          />
                          {img && img.trim() && (
                            <img
                              src={img}
                              alt="Preview"
                              className="mt-2 h-16 w-24 object-cover rounded-lg border border-slate-200"
                              loading="lazy"
                              onError={(e) => e.target.style.display = "none"}
                            />
                          )}
                        </div>
                        {projectForm.images.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeImageField(index)}
                            className="p-3 text-slate-400 hover:text-red-600 transition-colors"
                          >
                            <X size={20} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={addImageField}
                    className="mt-3 flex items-center gap-2 text-gold hover:text-gold-dark transition-colors"
                  >
                    <Plus size={18} /> Add Another Image
                  </button>
                </div>

                {/* Submit */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowProjectModal(false)}
                    className="flex-1 py-3 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!isProjectFormValid()}
                    className="flex-1 py-3 bg-gold text-navy font-bold rounded-lg hover:bg-gold-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {editingProject ? "Update Post" : "Create Post"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lot Modal */}
      <AnimatePresence>
        {showLotModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowLotModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-navy">
                  {editingLot ? "Edit Lot" : "Add New Lot"}
                </h2>
                <button onClick={() => setShowLotModal(false)} className="text-slate-400 hover:text-slate-600">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleLotSubmit} className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Title *</label>
                  <input
                    type="text"
                    value={lotForm.title}
                    onChange={(e) => setLotForm({ ...lotForm, title: e.target.value })}
                    placeholder="e.g. 1,200x Hanwha Q Cells 450W — Grade A"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold"
                    required
                  />
                </div>

                {/* Brand & Wattage */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Brand *</label>
                    <input
                      type="text"
                      value={lotForm.brand}
                      onChange={(e) => setLotForm({ ...lotForm, brand: e.target.value })}
                      placeholder="e.g. Hanwha Q Cells"
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Wattage *</label>
                    <input
                      type="text"
                      value={lotForm.wattage}
                      onChange={(e) => setLotForm({ ...lotForm, wattage: e.target.value })}
                      placeholder="e.g. 450W"
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold"
                      required
                    />
                  </div>
                </div>

                {/* Quantity & Condition */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Quantity *</label>
                    <input
                      type="text"
                      value={lotForm.quantity}
                      onChange={(e) => setLotForm({ ...lotForm, quantity: e.target.value })}
                      placeholder="e.g. 1,200 panels"
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Condition *</label>
                    <input
                      type="text"
                      value={lotForm.condition}
                      onChange={(e) => setLotForm({ ...lotForm, condition: e.target.value })}
                      placeholder="e.g. Grade A used"
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold"
                      required
                    />
                  </div>
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Location *</label>
                  <input
                    type="text"
                    value={lotForm.location}
                    onChange={(e) => setLotForm({ ...lotForm, location: e.target.value })}
                    placeholder="e.g. Manchester, UK"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Description *</label>
                  <textarea
                    value={lotForm.description}
                    onChange={(e) => setLotForm({ ...lotForm, description: e.target.value })}
                    placeholder="Describe the lot — origin, inspection notes, packaging, logistics details, anything a buyer needs to know."
                    rows={6}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold resize-y"
                    required
                  />
                </div>

                {/* Photos */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Photos</label>
                  <div className="space-y-3">
                    {lotForm.photos.map((img, index) => (
                      <div key={index} className="flex gap-2">
                        <div className="flex-1">
                          <input
                            type="url"
                            value={img}
                            onChange={(e) => updatePhotoField(index, e.target.value)}
                            placeholder="Paste photo URL here"
                            className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold"
                          />
                          {img && img.trim() && (
                            <img
                              src={img}
                              alt="Preview"
                              className="mt-2 h-16 w-24 object-cover rounded-lg border border-slate-200"
                              loading="lazy"
                              onError={(e) => e.target.style.display = "none"}
                            />
                          )}
                        </div>
                        {lotForm.photos.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removePhotoField(index)}
                            className="p-3 text-slate-400 hover:text-red-600 transition-colors"
                          >
                            <X size={20} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={addPhotoField}
                    className="mt-3 flex items-center gap-2 text-gold hover:text-gold-dark transition-colors"
                  >
                    <Plus size={18} /> Add Another Photo
                  </button>
                </div>

                {/* Submit */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowLotModal(false)}
                    className="flex-1 py-3 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!isLotFormValid()}
                    className="flex-1 py-3 bg-gold text-navy font-bold rounded-lg hover:bg-gold-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {editingLot ? "Update Lot" : "Create Lot"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* New Lot Access Code Modal */}
      <AnimatePresence>
        {newLotCode && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setNewLotCode(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-8 w-full max-w-md text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <CheckCircle size={40} className="text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-navy mb-2">Lot Created</h2>
              <p className="text-slate-500 mb-6">
                Share this access code with the buyer. It's the only way to reach this lot's private portal page.
              </p>

              <div className="flex items-center justify-center gap-3 bg-slate-50 border border-slate-200 rounded-xl py-6 px-4 mb-6">
                <span className="font-mono text-3xl font-bold text-navy tracking-widest" data-testid="new-lot-access-code">
                  {newLotCode}
                </span>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => copyAccessCode(newLotCode)}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-gold text-navy font-bold rounded-lg hover:bg-gold-light transition-colors"
                >
                  <Copy size={18} /> Copy Code
                </button>
                <button
                  onClick={() => setNewLotCode(null)}
                  className="flex-1 py-3 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Done
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Offers Modal */}
      <AnimatePresence>
        {showOffersModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowOffersModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-navy">Offers</h2>
                  {offersLot && (
                    <p className="text-slate-500 text-sm mt-1">
                      {offersLot.title} · <span className="font-mono">{offersLot.access_code}</span>
                    </p>
                  )}
                </div>
                <button onClick={() => setShowOffersModal(false)} className="text-slate-400 hover:text-slate-600">
                  <X size={24} />
                </button>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">BUYER</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">PRICE/PIECE</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">TOTAL</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">QTY WANTED</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">MESSAGE</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">SUBMITTED</th>
                    </tr>
                  </thead>
                  <tbody>
                    {offersLoading ? (
                      <tr>
                        <td colSpan={6} className="text-center py-12 text-slate-400">Loading...</td>
                      </tr>
                    ) : offers.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-12 text-slate-400">No offers submitted yet</td>
                      </tr>
                    ) : (
                      offers.map((o) => (
                        <tr key={o.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-6 py-4 font-medium text-navy">{o.buyer_name}</td>
                          <td className="px-6 py-4 font-bold text-gold">£{o.offer_amount.toLocaleString()}</td>
                          <td className="px-6 py-4 font-bold text-navy">
                            {o.quantity_wanted
                              ? `£${(o.offer_amount * o.quantity_wanted).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                              : "—"}
                          </td>
                          <td className="px-6 py-4 text-slate-600">{o.quantity_wanted ?? "—"}</td>
                          <td className="px-6 py-4 text-slate-600 max-w-xs">
                            <p className="line-clamp-2">{o.message || "—"}</p>
                          </td>
                          <td className="px-6 py-4 text-slate-500 text-sm whitespace-nowrap">{formatDate(o.submitted_at)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPage;
